import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  TextField,
  Alert,
  CircularProgress,
  Paper,
} from "@mui/material";
import { PersonAdd, Edit, Delete, Search } from "@mui/icons-material";
import apiClient from "../utils/apiClient";

const GardenShareModal = ({ open, onClose, garden }) => {
  const [users, setUsers] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState("viewer");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (open && garden) {
      fetchGardenUsers();
    }
  }, [open, garden]);

  const fetchGardenUsers = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/gardens/${garden.garden_uuid}/users`);
      setUsers(response.data.users);
    } catch (err) {
      setError("Failed to fetch garden users");
      console.error("Failed to fetch garden users:", err);
    } finally {
      setLoading(false);
    }
  };

  const searchUsers = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    try {
      const response = await apiClient.get(`/users/search?q=${encodeURIComponent(query)}`);
      setSearchResults(response.data);
    } catch (err) {
      console.error("Failed to search users:", err);
    }
  };

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    searchUsers(query);
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setSearchQuery(user.username);
    setSearchResults([]); // Clear search results
  };

  const addUserToGarden = async () => {
    if (!selectedUser) return;
    
    try {
      setLoading(true);
      await apiClient.post(`/gardens/${garden.garden_uuid}/users`, {
        user_uuid: selectedUser.user_uuid,
        role: selectedRole,
      });
      
      setSuccess(`User ${selectedUser.username} added successfully`);
      setSelectedUser(null);
      setSearchQuery("");
      setSearchResults([]);
      fetchGardenUsers(); // Refresh the list
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to add user");
      console.error("Failed to add user:", err);
    } finally {
      setLoading(false);
    }
  };

  const updateUserRole = async (userUuid, newRole) => {
    try {
      setLoading(true);
      await apiClient.put(`/gardens/${garden.garden_uuid}/users/${userUuid}`, {
        role: newRole,
      });
      
      setSuccess("User role updated successfully");
      fetchGardenUsers(); // Refresh the list
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update user role");
      console.error("Failed to update user role:", err);
    } finally {
      setLoading(false);
    }
  };

  const removeUserFromGarden = async (userUuid, username) => {
    if (!window.confirm(`Are you sure you want to remove ${username} from this garden?`)) {
      return;
    }
    
    try {
      setLoading(true);
      await apiClient.delete(`/gardens/${garden.garden_uuid}/users/${userUuid}`);
      
      setSuccess(`User ${username} removed successfully`);
      fetchGardenUsers(); // Refresh the list
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to remove user");
      console.error("Failed to remove user:", err);
    } finally {
      setLoading(false);
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'owner': return 'primary';
      case 'editor': return 'secondary';
      case 'viewer': return 'default';
      default: return 'default';
    }
  };

  const handleClose = () => {
    setUsers([]);
    setSearchResults([]);
    setSearchQuery("");
    setSelectedUser(null);
    setError(null);
    setSuccess(null);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      className="garden-share-modal"
    >
      <DialogTitle className="garden-share-modal-title">
        Share Garden: {garden?.name}
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Add User Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Add User
          </Typography>
          
          <Paper 
            className="add-user-box"
            sx={{ 
              p: 2, 
              border: '2px solid var(--color-border-vintage)',
              background: 'var(--color-aged-paper)',
              borderRadius: 0,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Search Field */}
              <Box sx={{ position: 'relative' }}>
                <TextField
                  fullWidth
                  label="Search users by username or email"
                  variant="outlined"
                  size="small"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="Type to search for users..."
                  className="garden-share-search-field"
                />
                
                {/* Search Results Dropdown */}
                {searchResults.length > 0 && (
                  <Box
                    className="garden-share-search-results"
                    sx={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      zIndex: 1000,
                      backgroundColor: 'var(--color-aged-paper)',
                      border: '1px solid var(--color-border-vintage)',
                      borderTop: 'none',
                      borderRadius: '0 0 4px 4px',
                      maxHeight: 200,
                      overflow: 'auto',
                      boxShadow: 2
                    }}
                  >
                    {searchResults.map((user) => (
                      <Box
                        key={user.user_uuid}
                        sx={{
                          padding: 1,
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'rgba(201, 169, 97, 0.1)' },
                          borderBottom: '1px solid var(--color-border-vintage)'
                        }}
                        onClick={() => handleUserSelect(user)}
                      >
                        <Typography variant="body1" sx={{ fontFamily: 'var(--font-serif)' }}>
                          {user.username}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'var(--font-serif)' }}>
                          {user.email}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
              
              {/* Role Field */}
              <TextField
                select
                fullWidth
                label="Role"
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                size="small"
                SelectProps={{ native: true }}
                className="garden-share-role-field"
              >
                <option value="viewer">Viewer</option>
                <option value="editor">Editor</option>
              </TextField>
              
              {/* Add Button */}
              <Button
                variant="contained"
                startIcon={<PersonAdd />}
                onClick={addUserToGarden}
                disabled={!selectedUser || loading}
                className="garden-share-add-button"
                sx={{
                  mt: 1,
                  fontFamily: 'var(--font-serif)',
                }}
              >
                Add User
              </Button>
            </Box>
          </Paper>
        </Box>

        {/* Current Users Section */}
        <Typography variant="h6" gutterBottom>
          Current Users
        </Typography>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress />
          </Box>
        ) : (
          <List>
            {users.map((user) => (
              <ListItem 
                key={user.user_uuid} 
                divider
                className="garden-share-user-item"
                sx={{
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: { xs: 1, sm: 0 }
                }}
              >
                <ListItemText
                  primary={user.username}
                  secondary={user.email}
                  sx={{ flex: { xs: '1 1 100%', sm: '1 1 auto' } }}
                />
                
                <Box 
                  className="garden-share-user-actions"
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    width: { xs: '100%', sm: 'auto' },
                    justifyContent: { xs: 'flex-start', sm: 'flex-end' }
                  }}
                >
                  <Chip
                    label={user.role}
                    color={getRoleColor(user.role)}
                    size="small"
                  />
                  
                  {user.role !== 'owner' && (
                    <>
                      <IconButton
                        size="small"
                        onClick={() => {
                          const newRole = user.role === 'editor' ? 'viewer' : 'editor';
                          updateUserRole(user.user_uuid, newRole);
                        }}
                        disabled={loading}
                      >
                        <Edit />
                      </IconButton>
                      
                      <IconButton
                        size="small"
                        onClick={() => removeUserFromGarden(user.user_uuid, user.username)}
                        disabled={loading}
                      >
                        <Delete />
                      </IconButton>
                    </>
                  )}
                </Box>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      
      <DialogActions className="garden-share-modal-actions">
        <Button onClick={handleClose} className="garden-share-close-button">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GardenShareModal;
